<template>
  <div class="pos-container">
    <vue-drawer-layout
      ref="drawer"
      :drawer-width="320"
      :enable="true"
      :backdrop="true"
      @mask-click="closeDrawer"
      @slide-end="onDrawerSlideEnd"
    >

      <div slot="drawer">
        <NavigationDrawer :close="() => closeDrawer()" class="navigation-drawer" />
      </div>

      <div slot="content" class="root-pane">
        <!-- Network Status Banner -->
        <div class="network-status">
          <InternetOnOff />
        </div>

        <transition name="slide-down">
          <div v-if="showChangeNotification" class="change-notification-banner">
            <div class="change-content">
              <div class="change-header">
                <div class="change-label">CHANGE DUE</div>
                <div v-if="lastSaleInvoice" class="invoice-label">Invoice: {{ lastSaleInvoice }}</div>
              </div>
              <div class="change-amount">
                {{ $utils.formatMoney(lastSaleChange, 2, '.', ',', true) }}
              </div>
              <div class="banner-actions">
                <button
                  class="reprint-btn"
                  @click="reprintLastReceipt"
                  :disabled="!isOnline"
                  :title="isOnline ? 'Reprint last receipt' : 'Reprint unavailable while offline'"
                >
                  <i class="fas fa-print" aria-hidden="true"></i>
                  <span>Reprint</span>
                </button>
                <button class="dismiss-btn" @click="dismissChange">OK</button>
              </div>
            </div>
          </div>
        </transition>
        
        <!-- Small screens layout -->
        <NavigationBar :toggle="openDrawer" class="nav" v-if="smallScreen" />
        <div class="navigation-route-view">
          <div class="mobile-layout" v-if="smallScreen">
            <NewProductCategories />
            <div class="mobile-search-container">
              <MobileSearchedProducts :showMainCategories="showCategories" @open-instruction="openInstruction" />
            </div>
          </div>

          <!-- Medium and large screens - Modern Grid Layout -->
          <div class="desktop-layout" v-if="!smallScreen">
            <div class="left-panel">
              <div class="categories-section">
                <ProductCategories class="categories-content" />
              </div>
              
              <div class="cart-section">
                <div class="section-header">
                  <h3 class="section-title">Sale Line</h3>
                </div>
                <NewShoppingChart class="cart-content" />
              </div>
            </div>
            
            <div class="right-panel">
              <div class="products-section">
                <SearchedProducts class="products-content" @open-instruction="openInstruction" />
              </div>
            </div>
            
            <FloatingInfo icon="fa-solid fa-bars" @click="openDrawer" class="menu-button" :disabled="hasSaleItems || isTabOpen" />
          </div>
        </div>
        <!-- Floating Orders Notification Bell -->
        <div class="floating-orders-bell" @click="toggleOrdersSheet" v-if="pendingCount > 0">
          <font-awesome-icon icon="fa-solid fa-bell" class="bell-icon" />
          <div class="orders-count-badge">{{ pendingCount }}</div>
        </div>
        
        <!-- Orders Sheet -->
        <OrdersSheet 
          :open="ordersSheetOpen" 
          :orders="pendingOrders" 
          @close="closeOrdersSheet"
          @accept="handleAccept"
          @reject="handleReject"
          @adjust="handleAdjust"
          @update-status="handleUpdateStatus"
        />
        
        <!-- Cooking Instruction Modal -->
        <CookingInstructionModal 
          v-if="showInstructionModal" 
          :plu="currentInstructionPlu"
          :cookingLink="currentInstructionCookingLink"
          :saleNumber="saleNumber"
          @close="closeInstructionModal"
        />
      </div>
    </vue-drawer-layout>
  </div>
</template>

<script>
import FloatingInfo from "@/components/layout/utils/FloatingInfo.vue";
import NewShoppingChart from "@/components/point-of-sale/sales/pos/NewShoppingChart.vue";
import ProductCategories from "@/components/point-of-sale/sales/pos/ProductCategories.vue";
import SearchedProducts from "@/components/point-of-sale/sales/pos/SearchedProducts.vue";
import MobileSearchedProducts from "@/components/point-of-sale/sales/pos/MobileSearchedProducts.vue";
import ShoppingCart from "@/components/point-of-sale/sales/pos/ShoppingCart.vue";
import NewProductCategories from "@/components/point-of-sale/sales/pos/NewProductCategories.vue";
import NavigationDrawer from "@/components/layout/navigation/NavigationDrawer.vue";
import InternetOnOff from "@/components/layout/NetworkChecker.vue";
import NavigationBar from "@/components/layout/navigation/NavigationBar.vue";
import CookingInstructionModal from "@/components/point-of-sale/sales/cooking-instructions/CookingInstructionModal.vue";
import OrdersSheet from "@/components/orders/OrdersSheet.vue";
import { toggleTheme } from "@/assets/js/theme";
import { mapGetters } from 'vuex';

export default {
  name: "POSView",
  components: {
    NavigationBar, InternetOnOff, FloatingInfo,
    NavigationDrawer, ShoppingCart, SearchedProducts, MobileSearchedProducts, ProductCategories, NewProductCategories, NewShoppingChart,
    CookingInstructionModal, OrdersSheet
  },
  computed: {
    ...mapGetters('orders', ['getPendingOrders', 'getPendingCount']),
    smallScreen() {
      // Force desktop layout for /pos route.
      // Mobile devices are redirected to /mobile-pos by router.
      return false;
    },
    hasSaleItems() {
      return this.$store.getters.getSaleLines && this.$store.getters.getSaleLines.length > 0;
    },
    isTabOpen() {
      // Check both isTabOpen flag AND verify there's an actual open table name
      const isTabOpen = this.$store.getters.getIsTabOpen;
      const openTableName = this.$store.getters.getOpenTableName;
      // Only consider tab open if BOTH isTabOpen is true AND there's a table name
      return isTabOpen && openTableName && openTableName.length > 0;
    },
    shortcuts() {
      return this.$store.getters.getShortcuts;
    },
    currentRoute() {
      return this.$route.name;
    },
    customer() {
      return this.$store.getters.getCustomer;
    },
    isSaleProcessing() {
      return this.$store.getters.getSaleProcessingState === true;
    },
    pendingOrders() { return this.getPendingOrders; },
    pendingCount() { return this.getPendingCount; },
    saleLineCount() {
      return this.$store.getters.getSaleLines?.length || 0;
    },
    lastSaleChange() {
      return this.$store.getters.getLastSaleChange;
    },
    lastSaleInvoice() {
      return this.$store.getters.getLastSaleInvoice;
    },
    showChangeNotification() {
      return this.$store.getters.getShowChangeNotification;
    }
  },
  beforeMount() {
    // Safety: clear stuck isSavingTable after hang/crash path (mobile already does this).
    // Does not replace network timeouts — only recovers desktop after a prior stuck lock.
    // RESTAURANT-POS-Q: entering POS after cashup/login is the recovery moment.
    this.$store.dispatch('resetTableSavingState');
    this.getTableBySaleNumber();
  },
  mounted() {
    //window.addEventListener('beforeunload', this.handleBeforeUnload);
  //   window.addEventListener('pagehide', this.handlePageHide);

    // Add event listener for keyboard shortcuts
    window.addEventListener('keydown', this.handleKeyDown);
    if (typeof document !== 'undefined') {
      document.addEventListener('click', this.handleDocumentClick, true);
    }

    // Start polling for pending orders
    this.$store.dispatch('orders/startPolling');

    // Clear any stale alerts on mount
    this.$nextTick(() => {
      if (typeof Swal !== 'undefined') {
        Swal.close();
        Swal.getPopup()?.remove();
        Swal.getContainer()?.remove();
      }
      if (typeof toastr !== 'undefined') {
        toastr.clear();
      }
      document.querySelectorAll('.modal, .modal-backdrop, .swal2-popup, .swal2-container, .sweet-alert, [role="dialog"]').forEach(el => el.style.display = 'none');
    });
  },
  beforeDestroy() {
  //  window.removeEventListener('beforeunload', this.handleBeforeUnload);
   // window.removeEventListener('pagehide', this.handlePageHide);

    // Remove event listener
    window.removeEventListener('keydown', this.handleKeyDown);
    if (typeof document !== 'undefined') {
      document.removeEventListener('click', this.handleDocumentClick, true);
    }

    // Stop polling when leaving POS view
    this.$store.dispatch('orders/stopPolling');
  },
  data() {
    return {
      saleNumber: this.$route.query.saleNumber,
      dontReload: this.$route.query.dontReload,
      selectCategory: true,
      shouldSaveOnLeave: false,
      currentTheme: localStorage.getItem('theme') || 'light',
      isNavigatingToTables: false, // Guard flag to prevent double calls
      showInstructionModal: false,
      currentInstructionPlu: null,
      currentInstructionCookingLink: null,
      ordersSheetOpen: false,
      isDrawerOpen: false,
      // Connectivity state for the change-notification Reprint button.
      // Read once at component creation; matches the simplest pattern used
      // in ViewInvoicesView.vue (data prop initialized from navigator.onLine).
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    }
  },
  watch: {
    saleLineCount(newVal, oldVal) {
      if (this.showChangeNotification && oldVal !== undefined && newVal > oldVal) {
        this.dismissChange();
      }
    }
  },
  methods: {
    dismissChange() {
      this.$store.commit('clearLastSaleChange');
    },
    /**
     * Reprint the most recent invoice/receipt from the change-notification banner.
     *
     * Reuses the same Vuex action (`reprintLastInvoice`) that the
     * "Reprint Receipt" button on `ViewInvoicesView.vue` dispatches, so the
     * reprint workflow has a single source of truth in the store.
     *
     * Disabled by the template when `navigator.onLine === false`, so the
     * action only runs when connectivity is available.
     *
     * @returns {Promise} Resolves with the result of the store dispatch.
     */
    reprintLastReceipt() {
      return this.$store.dispatch('reprintLastInvoice');
    },
    openInstruction(item, cookingLink) {
      this.currentInstructionPlu = item.PLU;
      this.currentInstructionCookingLink = cookingLink;
      this.showInstructionModal = true;
    },
    closeInstructionModal() {
      this.showInstructionModal = false;
      this.currentInstructionPlu = null;
      this.currentInstructionCookingLink = null;
    },
    toggleTheme() {
      this.currentTheme = toggleTheme();
    },
    toggleOrdersSheet() {
      this.ordersSheetOpen = !this.ordersSheetOpen;
    },
    closeOrdersSheet() {
      this.ordersSheetOpen = false;
    },
    async handleAccept(order) {
      await this.$store.dispatch('orders/acceptOnlineOrder', order);
    },
    async handleReject({ orderId, reason }) {
      await this.$store.dispatch('orders/rejectOrder', { orderId, reason });
    },
    async handleAdjust({ orderId, payload }) {
      try {
        await this.$store.dispatch('orders/adjustItems', { orderId, payload });
        if (typeof Swal !== 'undefined') {
          Swal.fire({
            icon: 'success',
            title: 'Order Adjusted',
            text: 'Order items were adjusted successfully.',
            timer: 1800,
            showConfirmButton: false
          });
        }
      } catch (error) {
        if (typeof Swal !== 'undefined') {
          Swal.fire({
            icon: 'error',
            title: 'Adjust Failed',
            text: error?.message || 'Failed to adjust order items.',
          });
        }
        throw error;
      }
    },
    async handleUpdateStatus({ orderId, status, notes }) {
      await this.$store.dispatch('orders/updateOrderStatus', { orderId, status, notes });
    },
    /**
     * Opens the nav drawer when the ref is mounted.
     * Sentry RESTAURANT-POS-5/B/J: cashup/login navigation can call this
     * before drawer exists — guard avoids TypeError on undefined.toggle.
     */
    openDrawer() {
      const drawer = this.$refs.drawer;
      if (drawer && typeof drawer.toggle === 'function') {
        drawer.toggle();
      }
      this.isDrawerOpen = true;
    },
    handleBeforeUnload(e) {
      if (this.$store.getters.getSaleLines && this.$store.getters.getSaleLines.length > 0) {

        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Do you want to save the table before leaving?';

        this.shouldSaveOnLeave = true;
        return e.returnValue;
      }
    },

    // Add this method to handle the actual saving when page is about to be hidden
    async handlePageHide() {
      if (this.shouldSaveOnLeave && this.$store.getters.getSaleLines && this.$store.getters.getSaleLines.length > 0) {
        try {
          // Prepare table data exactly like the updateTable action
          const saleLines = this.$store.getters.getSaleLines;
          const settings = this.$store.getters.getDeviceSettings || {};
          const user = this.$store.getters.getCurrentUser || {};

          // Calculate line totals like in the original updateTable action
          const processedSaleLines = saleLines.map(line => ({
            ...line,
            Line: (Number(line.Qty) * Number(line.Unit)) - Number(line.Discount)
          }));

          const tableData = {
            SaleNum: this.$store.getters.getCurrentSale,
            Name: this.$store.getters.getOpenTableName || `Table_${Date.now()}`,
            User: user.Username,
            PC: settings.PC,
            items: processedSaleLines,
            Customer: this.$store.getters.getCustomer,
            discount: parseFloat(this.$store.getters.getSaleDiscount?.total || 0),
          };

          const store = this.$store.getters.getDefaultStore || {};
          const apiUrl = `${serverApi}tables/${store.StoreDB}`;


          if (navigator.sendBeacon) {

            const jsonBlob = new Blob([JSON.stringify(tableData)], {
              type: 'application/json'
            });

            const success = navigator.sendBeacon(apiUrl, jsonBlob);

          } else {

            try {
              await this.$store.dispatch('updateTable', {
                name: tableData.Name,
                Customer: tableData.Customer
              });
            } catch (error) {
              console.error('Failed to save table:', error);
            }
          }


          this.$store.commit('clearSale');
          this.$store.commit(`setOpenTableName`, '')
          this.$store.commit('setIsTabOpen', false);
        } catch (error) {
          console.error('Error in handlePageHide:', error);
        }
      }
    },
    /**
     * Closes the nav drawer when the ref is mounted.
     * Sentry RESTAURANT-POS-5/B/J: layout without drawer or mid-unmount
     * must not throw on missing $refs.drawer.
     */
    closeDrawer() {
      const drawer = this.$refs.drawer;
      if (drawer && typeof drawer.toggle === 'function') {
        drawer.toggle(false);
      }
      this.isDrawerOpen = false;
    },
    onDrawerSlideEnd(visible) {
      this.isDrawerOpen = visible;
    },
    handleDocumentClick(event) {
      if (!this.isDrawerOpen) return;
      const drawerRoot = this.$refs.drawer?.$el;
      if (!drawerRoot) return;
      const drawerWrap = drawerRoot.querySelector('.drawer-wrap');
      if (drawerWrap && drawerWrap.contains(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      this.closeDrawer();
    },
    /**
     * Handle keyboard shortcuts
     * @param {KeyboardEvent} event - The keyboard event
     */
    handleKeyDown(event) {
      // Ignore shortcuts when typing in input fields
      const isTextInput = event.target && (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.isContentEditable);

      if (isTextInput) {
        return;
      }

      // Map special keys to standardized names
      const specialKeys = {
        ' ': 'SPACE',
        'Backspace': 'BACK_SPACE',
        'Tab': 'TAB',
        'Enter': 'ENTER',
        'Escape': 'ESCAPE',
        'End': 'END',
        'Home': 'HOME',
        'ArrowLeft': 'LEFT',
        'ArrowUp': 'UP',
        'ArrowRight': 'RIGHT',
        'ArrowDown': 'DOWN',
        'Delete': 'DELETE',
        'Insert': 'INSERT',
        'PageUp': 'PAGE_UP',
        'PageDown': 'PAGE_DOWN',
        'PrintScreen': 'PRINTSCREEN',
        '/': 'SLASH'
      };

      // Get standardized key name
      const keyName = specialKeys[event.key] || event.key.toUpperCase();

      // Check if shortcuts are loaded
      if (!this.shortcuts) {
        console.warn('[Shortcuts] Shortcuts not loaded yet');
        return;
      }

      // Find all actions mapped to this key
      const matchingActions = Object.keys(this.shortcuts).filter(
        action => this.shortcuts[action] === keyName
      );

      if (matchingActions.length === 0) return;

      if (this.isSaleProcessing) {
        event.preventDefault();
        console.log('[Shortcuts] Ignored while transaction is processing');
        return;
      }

      // Resolve conflicts based on current route context
      const shortcutAction = this.resolveShortcutConflict(matchingActions);

      if (shortcutAction) {
        event.preventDefault();
        console.log('[Shortcuts] Executing:', shortcutAction, 'Key:', keyName);
        this.executeShortcut(shortcutAction);
      }
    },
    /**
     * Resolve conflicts when multiple shortcuts are mapped to the same key
     * @param {Array} matchingActions - Array of shortcut action names
     * @returns {String} - The resolved shortcut action
     */
    resolveShortcutConflict(matchingActions) {
      if (matchingActions.length === 1) {
        return matchingActions[0];
      }

      const route = this.currentRoute;

      // Route-specific priority rules
      const priorityMap = {
        'pos': ['forexPay', 'Cash', 'Card', 'cgratePay', 'Payment', 'splitPay', 'Account',
                'SaveTable', 'Void', 'VoidAll', 'PriceCheck', 'PriceChange', 'LoadTable'],
        'tables': ['PaySupplier', 'MoneyToSafe'],
        'viewInvoice': ['Reprint', 'Refund']
      };

      const priorityActions = priorityMap[route];

      if (priorityActions) {
        const match = matchingActions.find(action => priorityActions.includes(action));
        if (match) return match;
      }

      // Return first match if no priority rule applies
      return matchingActions[0];
    },
    /**
     * Execute the shortcut action
     * @param {String} action - The shortcut action to execute
     */
    async executeShortcut(action) {
      const route = this.currentRoute;

      switch (action) {
        // ==================== FOCUS SHORTCUTS ====================
        case 'forexPay':
          if (route === 'pos') {
            this.findAndCallMethod('focusTotalGiven');
          }
          break;
        // ==================== PAYMENT SHORTCUTS ====================
        case 'Cash':
          if (route === 'pos') {
            this.findAndCallMethod('fastProcessTransaction', 'Cash');
          }
          break;

        case 'Card':
          if (route === 'pos') {
            this.findAndCallMethod('fastProcessTransaction', 'Card');
          }
          break;

        case 'splitPay':
          if (route === 'pos') {
            this.findAndCallMethod('goToSplitScreen');
          }
          break;

        case 'cgratePay':
          if (route === 'pos') {
            this.findAndCallMethod('kazangPayment');
          }
          break;

        case 'Payment':
          if (route === 'pos') {
            this.findAndCallMethod('process');
          }
          break;

        case 'Account':
          if (route === 'pos' && this.customer) {
            this.processAccountSale();
          } else if (route === 'pos' && !this.customer) {
            this.$utils.showWarning('Please select a customer for account sale');
          }
          break;

        // ==================== TABLE MANAGEMENT ====================
        case 'LoadTable':
          this.$router.push({ name: 'tables' });
          break;

        case 'SaveTable':
          if (route === 'pos') {
            this.findAndCallMethod('saveTable');
          }
          break;

        // ==================== TRANSACTION ACTIONS ====================
        case 'Void':
          if (route === 'pos') {
            this.voidLastItem();
          }
          break;

        case 'VoidAll':
          if (route === 'pos') {
            this.voidAllItems();
          }
          break;

        // ==================== PRICE OPERATIONS ====================
        case 'PriceCheck':
          if (route === 'pos') {
            this.findAndCallMethod('openItemSearchModal');
          }
          break;

        case 'PriceChange':
          if (route === 'pos') {
            this.findAndCallMethod('editSelectedLinePrice');
          }
          break;

        // ==================== DRAWER OPERATIONS ====================
        case 'OpenDrwer':
          const isAllowed = await this.$store.dispatch('validateSecurityPermission', 'OpenDrwer');
          if (isAllowed) {
            await this.$store.dispatch('openCashDrawer');
          }
          break;

        // ==================== INVOICE OPERATIONS ====================
        case 'Reprint':
          if (route === 'viewInvoice') {
            this.findAndCallMethod('reprintInvoice');
          }
          break;

        case 'Refund':
          if (route === 'viewInvoice') {
            this.findAndCallMethod('openRefund');
          }
          break;

        // ==================== TABLE SCREEN OPERATIONS ====================
        case 'PaySupplier':
          if (route === 'tables') {
            this.findAndCallMethod('doPayout');
          }
          break;

        case 'MoneyToSafe':
          if (route === 'tables') {
            this.findAndCallMethod('cashDrop');
          }
          break;

        // ==================== NAVIGATION SHORTCUTS ====================
        case 'CashUp':
          this.$router.push({ name: 'cashup' });
          break;

        case 'LogOff':
          // Confirm before logging out
          if (typeof Swal !== 'undefined') {
            Swal.fire({
              title: 'Log out?',
              text: 'Are you sure you want to log out of POS?'
              , icon: 'question',
              showCancelButton: true,
              confirmButtonText: 'Yes, log out',
              cancelButtonText: 'Cancel',
              allowOutsideClick: false,
              allowEscapeKey: false
            }).then((res) => {
              if (res.isConfirmed) {
                this.$store.dispatch('posLogout');
              }
            });
          } else {
            if (confirm('Are you sure you want to log out of POS?')) {
              this.$store.dispatch('posLogout');
            }
          }
          break;

        case 'ExitPos':
          // Confirm before closing the application
          if (typeof Swal !== 'undefined') {
            Swal.fire({
              title: 'Exit application?',
              text: 'Are you sure you want to exit Lyt-ning Resturant?'
              , icon: 'warning',
              showCancelButton: true,
              confirmButtonText: 'Yes, exit',
              cancelButtonText: 'Cancel',
              allowOutsideClick: false,
              allowEscapeKey: false
            }).then((res) => {
              if (res.isConfirmed) {
                this.closeApplication();
              }
            });
          } else {
            if (confirm('Are you sure you want to exit Lyt-ning Resturant?')) {
              this.closeApplication();
            }
          }
          break;

        // ==================== FUTURE IMPLEMENTATION ====================
        case 'CashBack':
          console.log('[Shortcuts] CashBack not yet implemented');
          break;

        default:
          console.warn('[Shortcuts] Unknown shortcut action:', action);
      }
    },
    /**
     * Close the application
     */
    closeApplication() {
      if (window.electron) {
        window.electron.closeApp();
      } else if (window.close) {
        window.close();
      } else {
        console.warn('[Shortcuts] Cannot close application - not in Electron or browser context');
      }
    },
    /**
     * Find and call a method in child components
     * Searches through the component tree to find and execute a method
     * @param {String} methodName - The name of the method to call
     * @param {...*} args - Arguments to pass to the method
     */
    findAndCallMethod(methodName, ...args) {
      // First try to find the method in the current view component
      const routerView = this.$el.querySelector('.navigation-route-view > *');
      if (routerView && routerView.__vue__ &&
          routerView.__vue__[methodName] &&
          typeof routerView.__vue__[methodName] === 'function') {
        routerView.__vue__[methodName](...args);
        return;
      }

      // If not found in the current view, search recursively through all components
      const findComponentWithMethod = (component) => {
        if (!component) return null;

        // Check if the component has the method
        if (component[methodName] && typeof component[methodName] === 'function') {
          return component;
        }

        // Check children recursively
        if (component.$children && component.$children.length > 0) {
          for (const child of component.$children) {
            const found = findComponentWithMethod(child);
            if (found) return found;
          }
        }

        return null;
      };

      const component = findComponentWithMethod(this.$root);
      if (component) {
        component[methodName](...args);
      } else {
        console.warn(`[Shortcuts] Method ${methodName} not found in any component`);
      }
    },
    /**
     * Void the last item in the current sale
     */
    voidLastItem() {
      const saleLines = this.$store.getters.getSaleLines;
      if (saleLines && saleLines.length > 0) {
        const lastItem = saleLines[saleLines.length - 1];
        this.$store.commit('voidLine', lastItem);
        console.log('[Shortcuts] Voided last item:', lastItem.Description);
      } else {
        console.warn('[Shortcuts] No items to void');
      }
    },
    /**
     * Process an account sale for the selected customer
     */
    processAccountSale() {
      if (this.customer) {
        this.findAndCallMethod('processTransaction', 'AccountSale');
      } else {
        this.$utils.showWarning('Please select a customer for account sale');
      }
    },
    /**
     * Void all items in the current sale
     */
    voidAllItems() {
      const saleLines = this.$store.getters.getSaleLines;
      if (saleLines && saleLines.length > 0) {
        const itemCount = saleLines.length;
        saleLines.forEach(item => {
          this.$store.commit('voidLine', item);
        });
        console.log(`[Shortcuts] Voided all ${itemCount} items`);
      } else {
        console.warn('[Shortcuts] No items to void');
      }
    },
    async getTableBySaleNumber() {
      // Close any stale dialogs/toasts when loading table
      if (typeof Swal !== 'undefined') {
        // Close all types of Swal (modal, toast)
        Swal.close();
        Swal.getPopup()?.remove();
        Swal.getContainer()?.remove();
      }
      // Close Toastr notifications if they exist
      if (typeof toastr !== 'undefined') {
        toastr.clear();
      }
      // Force hide any modal overlays
      document.querySelectorAll('.modal, .modal-backdrop, .swal2-popup, .swal2-container, .sweet-alert, [role="dialog"]').forEach(el => el.style.display = 'none');

      if (this.dontReload || !this.saleNumber) {
        await this.$store.dispatch('loadFromLocalStorage')
        return
      };
      console.log('[TableFlow][POSView:getTableBySaleNumber:start]', {
        routeSaleNumber: this.saleNumber,
        currentSaleBefore: this.$store.getters.getCurrentSale,
        dontReload: this.dontReload
      });
      this.$utils.showLoading();
      try {
        await this.$store.dispatch(`getTableBySaleNumber`, this.saleNumber);
        console.log('[TableFlow][POSView:getTableBySaleNumber:done]', {
          routeSaleNumber: this.saleNumber,
          currentSaleAfter: this.$store.getters.getCurrentSale
        });
      } catch (error) {
        // Error already handled by store, but we catch here to prevent unhandled promise rejection
        console.warn('[POSView] Table loading error:', error);
      } finally {
        this.$utils.hideLoading();
      }
    },
    showCategories(value) {
      if (this.smallScreen) {
        this.selectCategory = value
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    // If we're already navigating to tables, allow it without processing
    if (this.isNavigatingToTables) {
      console.log('[POSView] Already navigating to tables, allowing navigation');
      return next();
    }

    // Allow navigation freely if there are no sale lines at all
    if (this.$store.getters.getSaleLines.length === 0) return next();
    
    // Allow navigation to specific routes without prompting
    if (to.name === `instruction` || to.name === `payment` || to.name === `select-customer`
      || (to.name === `poslogin` && this.$store.getters.getSaleLines.length === 0)) {
      return next();
    } 
    
    // Handle navigation to tables view
    if (to.name === `tables`) {
      const existingTableName = this.$store.getters.getOpenTableName;
      
      if (existingTableName) {
        // Table already exists - save/update silently without prompt
        this.$utils.showLoading();
        
        return this.$store.dispatch('updateTable', {
          name: existingTableName,
          Customer: this.$store.getters.getCustomer,
        }).then((result) => {
          // RESTAURANT-POS-Q: soft-skip — do not clear cart or leave while first save runs
          if (result && result.skipped) {
            this.$utils.hideLoading();
            next(false);
            return;
          }
          this.$store.dispatch("clearLocalStorage");
          this.$store.commit('clearSale');
          this.$utils.hideLoading();

          // Prevent re-saving when proceedToSales triggers a new route change.
          this.isNavigatingToTables = true;
          next(false);
          return this.$store.dispatch('proceedToSales');
        }).catch(error => {
          this.$utils.hideLoading();
          console.error('[POSView] Error updating table:', error);
          responses.showError('Failed to save table: ' + (error.message || error));
        });
      } else {
        // No table name - prompt for it once
        this.$store.commit('setIsTabOpen', true);
        
        return Swal.fire({
          title: 'Table Name',
          icon: 'question',
          input: 'text',
          showCancelButton: true,
          confirmButtonText: 'Save Table',
          showLoaderOnConfirm: true,
          allowOutsideClick: false,
          preConfirm: (input) => {
            if (!input) return Swal.showValidationMessage(`Table name is required`);
            
            // Set the table name
            this.$store.commit(`setOpenTableName`, input);
            
            // Save the table
            return this.$store.dispatch('saveNewTable', {
              name: input,
              Customer: this.$store.getters.getCustomer,
            }).then((saveResult) => {
              // RESTAURANT-POS-Q: soft-skip keeps dialog open with wait message
              if (saveResult && saveResult.skipped) {
                return Swal.showValidationMessage('Save already in progress, please wait');
              }
              return input;
            })
              .catch(error => {
                console.error('[POSView] Error saving table:', error);
                return Swal.showValidationMessage(error.message || 'Failed to save table');
              });
          },
        }).then((result) => {
          this.$store.commit('setIsTabOpen', false);
          
          if (result.isConfirmed) {
            // Success - clear and navigate
            this.$store.dispatch("clearLocalStorage");
            this.$store.commit('clearSale');

            // Prevent re-saving when proceedToSales triggers a new route change.
            this.isNavigatingToTables = true;
            next(false);
            
            // Show success without waiting for it
            Swal.fire({
              title: `Success`,
              icon: 'success',
              text: `Table ${result.value} Saved`,
              timer: 1500,
              showConfirmButton: false
            });
            
            return this.$store.dispatch('proceedToSales');
          }
          // If cancelled, don't navigate
        });
      }
    } else {
      // Navigating somewhere else with unsaved items
      return responses.showWarning(`Save or Use Current Table`);
    }
  }
}
</script>


<style scoped lang="scss">
// Modern POS Container
.pos-container {
  margin: 0;
  padding: 0;
  height: 100vh;
  background: var(--bg-gradient);
  overflow: hidden;
}

// Root Pane
.root-pane {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: transparent;
}

// Network Status Banner
.network-status {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 1000;
  width: 100%;
}

// Navigation Route View
.navigation-route-view {
  flex: 1;
  min-height: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.change-notification-banner {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 1200;
  background: linear-gradient(135deg, var(--accent-electric-blue), var(--accent-neon-purple));
  color: #fff;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(var(--accent-electric-blue-rgb), 0.35);
  padding: 12px 18px;
  min-width: 220px;
}

.change-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.change-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.change-label {
  font-size: 12px;
  letter-spacing: 1px;
  font-weight: 700;
}

.invoice-label {
  font-size: 11px;
  opacity: 0.9;
}

.change-amount {
  font-size: 28px;
  font-weight: 800;
  line-height: 1;
}

.dismiss-btn {
  margin-top: 6px;
  border: none;
  border-radius: 8px;
  background: var(--card-bg);
  color: var(--accent-electric-blue);
  font-weight: 700;
  padding: 6px 14px;
}

// Wrapper that holds the two banner action buttons (Reprint + OK) side by side.
.banner-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}

// Secondary-button look for the Reprint action on the change banner.
// Visual style intentionally mirrors the `.reprint-btn` used on
// ViewInvoicesView.vue so users see the same affordance in both places.
.reprint-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  border-radius: 8px;
  background: var(--card-bg);
  color: var(--accent-electric-blue);
  font-weight: 700;
  padding: 6px 12px;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.15s ease;

  i {
    font-size: 0.95rem;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
}

.slide-down-enter-active, .slide-down-leave-active {
  transition: all 0.25s ease;
}

.slide-down-enter, .slide-down-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

// Desktop Layout - Modern Grid
.desktop-layout {
  display: grid;
  grid-template-columns: 4fr 8fr;
  gap: 16px;
  padding: 5px;
  height: calc(100vh - 20px);
  overflow: hidden;
  position: relative;
}

// Left Panel (Categories + Cart)
.left-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  overflow: hidden;
}

.categories-section {
  flex: 0 0 45%;
  display: flex;
  flex-direction: column;
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--card-shadow);
  border: var(--card-border);
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: var(--card-shadow-hover);
  }
}

.cart-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--card-shadow);
  border: var(--card-border);
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: var(--card-shadow-hover);
  }
}

// Right Panel (Products)
.right-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.products-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--card-shadow);
  border: var(--card-border);
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: var(--card-shadow-hover);
  }
}

// Section Headers
.section-header {
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(var(--accent-primary-rgb), 0.08) 0%, rgba(var(--accent-primary-rgb), 0.04) 100%);
  border-bottom: 2px solid rgba(var(--accent-primary-rgb), 0.15);
}

.section-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

// Section Content Areas
.categories-content,
.cart-content,
.products-content {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

// Mobile Layout
.mobile-layout {
  padding: 8px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mobile-search-container {
  height: 50px;
}

// Navigation Drawer
.navigation-drawer {
  min-width: 320px;
  width: 100%;
  height: 100vh;
  background: var(--card-bg);
}

// Floating Menu Button
.menu-button {
  position: fixed;
  top: 56px;
  left: 0px;
  z-index: 999;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent-primary) 0%, #2980b9 100%);
  box-shadow: 0 8px 24px rgba(var(--accent-primary-rgb), 0.4);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: scale(1.1) rotate(90deg);
    box-shadow: 0 12px 32px rgba(var(--accent-primary-rgb), 0.5);
  }
  
  &:active {
    transform: scale(0.95) rotate(90deg);
  }
}

/* Floating Orders Notification Bell */
.floating-orders-bell {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, var(--accent-electric-blue) 0%, var(--accent-neon-purple) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(var(--accent-electric-blue-rgb), 0.4);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  animation: bellPulse 2s ease-in-out infinite;

  &:hover {
    transform: scale(1.1) translateY(-2px);
    box-shadow: 0 12px 32px rgba(var(--accent-electric-blue-rgb), 0.5);

    .bell-icon {
      animation: bellRing 0.5s ease-in-out;
    }
  }

  &:active {
    transform: scale(0.95);
  }

  .bell-icon {
    font-size: 1.75rem;
    color: white;
  }

  .orders-count-badge {
    position: absolute;
    top: -4px;
    right: -4px;
    min-width: 28px;
    height: 28px;
    background: var(--danger);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 700;
    font-size: 0.85rem;
    padding: 0 8px;
    border: 3px solid var(--bg-primary);
    box-shadow: 0 2px 8px rgba(var(--danger-rgb), 0.4);
    animation: badgePop 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
}

/* Animations */
@keyframes bellPulse {
  0%, 100% {
    box-shadow: 0 8px 24px rgba(var(--accent-electric-blue-rgb), 0.4);
  }
  50% {
    box-shadow: 0 8px 24px rgba(var(--accent-electric-blue-rgb), 0.6),
                0 0 0 8px rgba(var(--accent-electric-blue-rgb), 0.1);
  }
}

@keyframes bellRing {
  0%, 100% { transform: rotate(0deg); }
  10%, 30%, 50%, 70%, 90% { transform: rotate(-15deg); }
  20%, 40%, 60%, 80% { transform: rotate(15deg); }
}

@keyframes badgePop {
  0% { transform: scale(0); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}

// Responsive adjustments
@media (max-width: 1200px) {
  .desktop-layout {
    grid-template-columns: 340px 1fr;
    gap: 12px;
    padding: 12px;
  }
}

@media (max-width: 992px) {
  .desktop-layout {
    grid-template-columns: 300px 1fr;
    gap: 10px;
    padding: 10px;
  }
  
  .section-header {
    padding: 12px 16px;
  }
  
  .section-title {
    font-size: 16px;
  }
}

@media (max-width: 768px) {
  .pos-container {
    background: var(--bg-primary);
  }
  
  /* Adjust floating bell position on mobile */
  .floating-orders-bell {
    bottom: 80px; /* Above mobile bottom sheet */
    right: 16px;
    width: 56px;
    height: 56px;

    .bell-icon {
      font-size: 1.5rem;
    }

    .orders-count-badge {
      min-width: 24px;
      height: 24px;
      font-size: 0.75rem;
    }
  }
}

// Theme button (if needed)
.theme-button {
  top: 90px;
  right: 24px;
}

// Animations
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.categories-section,
.cart-section,
.products-section {
  animation: slideIn 0.4s ease-out;
}
</style>
